import type { ReactElement } from 'react';
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { format, isWithinInterval, parseISO } from 'date-fns';
import { Link } from 'react-router-dom';
import { Card, CardBody, CardHeader } from 'reactstrap';
import type { TestInstanceUser } from 'feature-components/assigned-test-instances/queries';
import PeriodBadge from 'feature-components/badges/period-badge/period-badge';
import ResponseStatusBadge from 'feature-components/badges/response-status-badge';
import { commonDateFormat } from 'helpers/date.helpers';
import { goToLearnerReport } from 'pages/reports/url.helpers';
import { AreaType, useArea } from 'services/assist-api/entities/area/areas';
import {
  isFinished,
  transformStatus
} from 'services/assist-api/entities/test-instance-response/test-instance-response';
import type { ResponseStatus } from 'services/assist-api/entities/test-instance-response/types';
import { useTest } from 'services/assist-api/entities/test/tests';

type StartContinueButtonProps = {
  token: StatusRowToken;
  status: ResponseStatus | undefined;
};

function StartContinueButton(props: StartContinueButtonProps): ReactElement {
  const { token, status } = props;
  return (
    <a
      href={`${NZCER_ONLINE}?token=${token.value}`}
      target="_blank"
      rel="noreferrer"
      className="ms-2 btn btn-outline-warning"
    >
      {status ? 'Continue' : 'Start'}
    </a>
  );
}

const isValidToken = (token?: { enabled: boolean; validFrom: string; validTo: string }): boolean =>
  !!token &&
  token.enabled &&
  isWithinInterval(new Date(), {
    start: parseISO(token.validFrom),
    end: parseISO(token.validTo)
  });

type StatusRowToken = { enabled: boolean; value: string; validFrom: string; validTo: string };

type StatusRowProps = {
  status?: ResponseStatus;
  token?: StatusRowToken;
  currentUserIsCustomerUser: boolean;
};

function StatusRow(props: StatusRowProps): ReactElement {
  const { status, token, currentUserIsCustomerUser } = props;
  return (
    <tr>
      <th scope="row">Status</th>
      <td>
        <ResponseStatusBadge>{transformStatus(status)}</ResponseStatusBadge>
        {!!token && isValidToken(token) && !isFinished(status) && currentUserIsCustomerUser && (
          <StartContinueButton token={token} status={status} />
        )}
      </td>
    </tr>
  );
}

function DateRow({
  isSchool,
  start,
  end
}: {
  isSchool?: boolean;
  start: string;
  end: string;
}): ReactElement {
  return (
    <tr>
      <th scope="row">Date</th>
      <td>
        {isSchool ? (
          <PeriodBadge startDate={start} endDate={end} showInputDates />
        ) : (
          `${format(new Date(start), commonDateFormat)} - ${format(new Date(end), commonDateFormat)}`
        )}
      </td>
    </tr>
  );
}

type Props = {
  testInstanceUser: TestInstanceUser;
  isSchool?: boolean;
  currentUserIsCustomerUser: boolean;
};

/**
 * Test Instance Card: used to show information about an assigned assessment
 * - prefers using response as primary record of tokens & status
 * - falls back on pure token data (may not have a response yet)
 * - supports no tokens (scanning data)
 * @param testInstanceUser
 * @param isSchool controls if dates are period or ranges
 * @param currentUserIsCustomerUser
 */
export default function TestInstanceCard(props: Props): ReactElement {
  const {
    testInstanceUser: { testInstance, responses, tokens, ...testInstanceUser },
    isSchool,
    currentUserIsCustomerUser
  } = props;

  const area = useArea(testInstance.area.id);
  const test = useTest(testInstance.test.id);

  return (
    <Card className={clsx('mb-2', area.data?.json.identifier)} data-testid="test-instance-card">
      <CardHeader>
        <h4 className="h6 p-0 m-0">{testInstance.name}</h4>
      </CardHeader>
      <CardBody>
        <table className="table table-sm">
          <tbody>
            <tr>
              <th scope="row">Test</th>
              <td>{test.data?.json.name}</td>
            </tr>

            {/* Try use responses are source of truth, as these have actually been used */}
            {responses?.map((response) => {
              const token = tokens?.find((t) => t.id === response.token?.id);
              return (
                <React.Fragment key={`response-${response.id}`}>
                  {token ? (
                    <>
                      <tr>
                        <th scope="row">Token</th>
                        <td>{token.value}</td>
                      </tr>
                      <DateRow isSchool={isSchool} start={token.validFrom} end={token.validTo} />
                    </>
                  ) : (
                    <DateRow
                      isSchool={isSchool}
                      start={testInstance.startDate}
                      end={testInstance.endDate}
                    />
                  )}
                  <StatusRow
                    status={response?.status}
                    token={token}
                    currentUserIsCustomerUser={currentUserIsCustomerUser}
                  />
                  {/* Link to individual report for assessments */}
                  {area.data?.json.type === AreaType.Assessment && (
                    <tr>
                      <th scope="row">Report</th>
                      <td>
                        <Link
                          to={goToLearnerReport(testInstanceUser.id)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <FontAwesomeIcon icon="chart-bar" /> View Report
                        </Link>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}

            {/* When no responses just show token info */}
            {!responses?.length &&
              tokens?.map((token) => (
                <React.Fragment key={`token-${token.id}`}>
                  <tr>
                    <th scope="row">Token</th>
                    <td>{token.value}</td>
                  </tr>
                  <DateRow isSchool={isSchool} start={token.validFrom} end={token.validTo} />
                  <StatusRow token={token} currentUserIsCustomerUser={currentUserIsCustomerUser} />
                </React.Fragment>
              ))}
          </tbody>
        </table>
      </CardBody>
    </Card>
  );
}
